import { lazy, Suspense, useEffect, useRef, useState, useCallback } from 'react';
/// Components
import Index from './jsx/index';
import { connect, useDispatch } from 'react-redux';
import {  Route, Routes, useLocation , useNavigate , useParams } from 'react-router-dom';
// action
import { checkAutoLogin, Logout } from './store/actions/AuthActions';
import { isAuthenticated } from './store/selectors/AuthSelectors';
/// Style

import "./css/style.css";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


const Login = lazy(() => {
    return new Promise(resolve => {
		setTimeout(() => resolve(import('./jsx/pages/Login')), 500);
	});
});

function withRouter(Component) {
    function ComponentWithRouterProp(props) {
      let location = useLocation();
      let navigate = useNavigate();
      let params = useParams();
      
      return (
        <Component
          {...props}
          router={{ location, navigate, params }}
        />
      );
    }
  
    return ComponentWithRouterProp;
}

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARN_BEFORE_MS  =  2 * 60 * 1000; // show warning 2 minutes before logout

function IdleWarningModal({ countdown }) {
    const mins = Math.floor(countdown / 60);
    const secs = countdown % 60;
    const display = mins > 0
        ? `${mins}:${String(secs).padStart(2, '0')}`
        : `${secs} second${secs !== 1 ? 's' : ''}`;
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div style={{
                background: '#fff', borderRadius: 10, padding: '32px 36px',
                maxWidth: 400, width: '90%',
                boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                textAlign: 'center',
            }}>
                <i className="las la-clock" style={{ fontSize: 48, color: '#f59e0b' }} />
                <h5 style={{ marginTop: 12, marginBottom: 6 }}>Session Timeout Warning</h5>
                <p style={{ color: '#555', marginBottom: 8 }}>
                    No activity detected. You will be logged out in{' '}
                    <strong style={{ color: countdown <= 30 ? '#dc3545' : '#343a40' }}>{display}</strong>.
                </p>
                <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 0 }}>
                    Move the cursor to stay logged in.
                </p>
            </div>
        </div>
    );
}

function App (props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // ── Idle timeout ────────────────────────────────────────────────────────────
    const idleTimerRef  = useRef(null);
    const warnTimerRef  = useRef(null);
    const countdownRef  = useRef(null);
    const warningOnRef  = useRef(false);   // avoids setState churn on frequent events
    const lastResetRef  = useRef(0);       // throttle activity handler to once/sec

    const [showWarning, setShowWarning] = useState(false);
    const [countdown,   setCountdown]   = useState(120);

    const doLogout = useCallback(() => {
        clearTimeout(idleTimerRef.current);
        clearTimeout(warnTimerRef.current);
        clearInterval(countdownRef.current);
        warningOnRef.current = false;
        setShowWarning(false);
        dispatch(Logout(navigate));
    }, [dispatch, navigate]);

    const resetIdleTimer = useCallback(() => {
        // Throttle: ignore if fired less than 1 s ago
        const now = Date.now();
        if (now - lastResetRef.current < 1000) return;
        lastResetRef.current = now;

        clearTimeout(idleTimerRef.current);
        clearTimeout(warnTimerRef.current);
        clearInterval(countdownRef.current);

        if (warningOnRef.current) {
            warningOnRef.current = false;
            setShowWarning(false);
        }

        // Show warning 2 minutes before the idle limit
        warnTimerRef.current = setTimeout(() => {
            warningOnRef.current = true;
            setShowWarning(true);
            setCountdown(WARN_BEFORE_MS / 1000);
            countdownRef.current = setInterval(() => {
                setCountdown(c => (c > 1 ? c - 1 : 0));
            }, 1000);
        }, IDLE_TIMEOUT_MS - WARN_BEFORE_MS);

        // Hard logout after full idle period
        idleTimerRef.current = setTimeout(doLogout, IDLE_TIMEOUT_MS);
    }, [doLogout]);

    // Start idle tracking when authenticated; clear on logout
    useEffect(() => {
        if (!props.isAuthenticated) {
            clearTimeout(idleTimerRef.current);
            clearTimeout(warnTimerRef.current);
            clearInterval(countdownRef.current);
            return;
        }
        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
        events.forEach(ev => window.addEventListener(ev, resetIdleTimer, { passive: true }));
        resetIdleTimer(); // arm the timer immediately
        return () => {
            events.forEach(ev => window.removeEventListener(ev, resetIdleTimer));
            clearTimeout(idleTimerRef.current);
            clearTimeout(warnTimerRef.current);
            clearInterval(countdownRef.current);
        };
    }, [props.isAuthenticated, resetIdleTimer]);
    // ── End idle timeout ────────────────────────────────────────────────────────

    useEffect(() => {
        checkAutoLogin(dispatch, navigate);
    }, [dispatch, navigate]);

    if (props.isAuthenticated) {
		return (
			<>
                {showWarning && (
                    <IdleWarningModal countdown={countdown} />
                )}
                <ToastContainer
                    position="top-right"
                    autoClose={4000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    pauseOnHover
                    theme="colored"
                />
                <Suspense fallback={
                    <div id="preloader">
                        <div className="sk-three-bounce">
                            <div className="sk-child sk-bounce1"></div>
                            <div className="sk-child sk-bounce2"></div>
                            <div className="sk-child sk-bounce3"></div>
                        </div>
                    </div>
                   }
                >
                    <Index />
                </Suspense>
            </>
        );

	}else{
		return (
			<div className="vh-100">
                <Suspense fallback={
                    <div id="preloader">
                        <div className="sk-three-bounce">
                            <div className="sk-child sk-bounce1"></div>
                            <div className="sk-child sk-bounce2"></div>
                            <div className="sk-child sk-bounce3"></div>
                        </div>
                    </div>
                  }
                >
                    <Routes>
                        <Route path='/login' element={<Login />} />
                    </Routes>
                </Suspense>
			</div>
		);
	}
};

const mapStateToProps = (state) => {
    return {
        isAuthenticated: isAuthenticated(state),
    };
};

export default withRouter(connect(mapStateToProps)(App)); 
